import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Search, Heart, Clock, FileText } from 'lucide-react';
import { Button } from './Button';

type EmptyStateType = 'no-recipes' | 'no-results' | 'no-favorites' | 'no-history' | 'no-timers' | 'empty';

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyState({
  type = 'empty',
  title,
  description,
  icon,
  action,
  secondaryAction,
}: EmptyStateProps) {
  const { t } = useTranslation('common');

  const presets: Record<EmptyStateType, { icon: React.ElementType; emoji: string; title: string; description: string }> = {
    'no-recipes': {
      icon: BookOpen,
      emoji: '\u{1F4DA}',
      title: t('empty.noRecipes.title'),
      description: t('empty.noRecipes.description'),
    },
    'no-results': {
      icon: Search,
      emoji: '\u{1F50D}',
      title: t('empty.noResults.title'),
      description: t('empty.noResults.description'),
    },
    'no-favorites': {
      icon: Heart,
      emoji: '\u{1F49D}',
      title: t('empty.noFavorites.title'),
      description: t('empty.noFavorites.description'),
    },
    'no-history': {
      icon: Clock,
      emoji: '\u23F0',
      title: t('empty.noHistory.title'),
      description: t('empty.noHistory.description'),
    },
    'no-timers': {
      icon: Clock,
      emoji: '\u23F1\uFE0F',
      title: t('empty.noTimers.title'),
      description: t('empty.noTimers.description'),
    },
    'empty': {
      icon: FileText,
      emoji: '\u{1F4C4}',
      title: t('empty.empty.title'),
      description: t('empty.empty.description'),
    },
  };

  const preset = presets[type];
  const Icon = preset.icon;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '48px 24px',
        maxWidth: '400px',
        margin: '0 auto',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '80px',
          height: '80px',
          borderRadius: 'var(--radius-2xl)',
          background: 'linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}
      >
        {icon ?? <Icon style={{ width: '36px', height: '36px', color: 'var(--accent-primary)' }} />}
        <span
          style={{
            position: 'absolute',
            bottom: '-4px',
            insetInlineEnd: '-4px',
            fontSize: '24px',
          }}
        >
          {preset.emoji}
        </span>
      </div>

      <h3
        style={{
          fontSize: '18px',
          fontWeight: 700,
          color: 'var(--text-primary)',
          margin: '0 0 8px 0',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        }}
      >
        {title ?? preset.title}
      </h3>

      <p
        style={{
          fontSize: '14px',
          color: 'var(--text-tertiary)',
          margin: '0 0 24px 0',
          lineHeight: 1.6,
        }}
      >
        {description ?? preset.description}
      </p>

      {(action || secondaryAction) && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {action && (
            <Button onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
